/**
 * AI Provider Factory
 * 
 * Factory class for creating and managing AI provider instances.
 * Uses the singleton pattern to reuse provider instances.
 */

import {
  AIProvider,
  IAIProvider,
  AIModelConfig
} from './AIProvider.types';
import { OpenAIProvider } from './providers/OpenAIProvider';
import { AnthropicProvider } from './providers/AnthropicProvider';
import { GoogleProvider } from './providers/GoogleProvider';
import { XAIProvider } from './providers/XAIProvider';
import { MODEL_CONFIGS, getModelConfig } from './config/modelConfig';

/**
 * Factory for creating and managing AI providers
 * 
 * Features:
 * - Singleton pattern for provider instances
 * - Automatic provider resolution from model ID
 * - Provider availability checking
 */
export class AIProviderFactory {
  private static providers: Map<AIProvider, IAIProvider> = new Map();
  
  /**
   * Get a provider instance by provider type
   * Creates the provider if it doesn't exist (lazy initialization)
   * 
   * @param providerType - The provider type to get
   * @returns The provider instance
   */
  static getProvider(providerType: AIProvider): IAIProvider {
    if (!this.providers.has(providerType)) {
      this.providers.set(providerType, this.createProvider(providerType));
    }
    // Safe to assert since we just set the value above
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.providers.get(providerType)!;
  }
  
  /**
   * Create a new provider instance
   * 
   * @param providerType - The provider type to create
   * @returns A new provider instance
   * @throws Error if provider type is unknown
   */
  private static createProvider(providerType: AIProvider): IAIProvider {
    switch (providerType) {
      case AIProvider.OPENAI:
        return new OpenAIProvider();
      case AIProvider.ANTHROPIC:
        return new AnthropicProvider();
      case AIProvider.GOOGLE:
        return new GoogleProvider();
      case AIProvider.XAI:
        return new XAIProvider();
      default:
        throw new Error(`Unknown provider: ${providerType}`);
    }
  }
  
  /**
   * Get the appropriate provider for a given model ID
   * 
   * @param modelId - The model ID to find a provider for
   * @returns The provider that supports this model
   * @throws Error if model is unknown
   */
  static getProviderForModel(modelId: string): IAIProvider {
    const config = MODEL_CONFIGS[modelId];
    if (!config) {
      throw new Error(`Unknown model: ${modelId}. Check modelConfig.ts for available models.`);
    }
    return this.getProvider(config.provider);
  }
  
  /**
   * Get the model configuration for a model ID
   * 
   * @param modelId - The model ID to look up
   * @returns The model configuration or undefined if not found
   */
  static getModelConfig(modelId: string): AIModelConfig | undefined {
    return getModelConfig(modelId);
  }
  
  /**
   * Get all available providers (those with valid credentials)
   * 
   * @returns Array of available provider types
   */
  static async getAvailableProviders(): Promise<AIProvider[]> {
    const available: AIProvider[] = [];
    
    for (const providerType of Object.values(AIProvider)) {
      try {
        const provider = this.getProvider(providerType);
        if (await provider.isAvailable()) {
          available.push(providerType);
        }
      } catch {
        // Provider not implemented yet, skip
        continue;
      }
    }
    
    return available;
  }
  
  /**
   * Check if a specific provider is available
   * 
   * @param providerType - The provider type to check
   * @returns Whether the provider is available
   */
  static async isProviderAvailable(providerType: AIProvider): Promise<boolean> {
    try {
      const provider = this.getProvider(providerType);
      return await provider.isAvailable();
    } catch {
      return false;
    }
  }
  
  /**
   * Get all models from all available providers
   * 
   * @returns Array of all model configurations
   */
  static async getAvailableModels(): Promise<AIModelConfig[]> {
    const availableProviders = await this.getAvailableProviders();
    const models: AIModelConfig[] = [];
    
    for (const providerType of availableProviders) {
      const provider = this.getProvider(providerType);
      models.push(...provider.getAvailableModels());
    }
    
    return models;
  }
  
  /**
   * Clear all cached provider instances
   * Useful for testing or when credentials change
   */
  static clearCache(): void {
    this.providers.clear();
  }
  
  /**
   * Clear a specific provider from cache
   * 
   * @param providerType - The provider to clear
   */
  static clearProvider(providerType: AIProvider): void {
    this.providers.delete(providerType);
  }
}
